import { IAppContext, IChannel } from '@byted-cg/linkchat-core';
import {
  BytedIM,
  Conversation,
  ExtensionPlugin,
  IMEvent,
  Message,
  MetricsPlugin,
  MultimediaPlugin,
  StoragePlugin,
  IMOption,
  InitResult,
  im_proto,
} from '@byted/im-web-sdk';

import { DIALOG_STATUS, DIALOG_TYPE } from '../../constants/status';
import { ChannelInitProps, CombineMessageType } from '../../lib/type';
import { isIndexedDBSupport } from '../../utils/support';
import { isUndefined } from 'lodash-es';
import { logger, monitor } from '../metrics';
import { ImcloudInfo } from '../../constants/metrics';

class Channel
  extends IChannel<IAppContext<CombineMessageType>>
  implements IChannel<any>
{
  private initLoadSize: number;
  private cursor?: Long.Long;
  private imcloudConfig?: IMOption;
  private appContext?: IAppContext<CombineMessageType>;
  public instance: BytedIM;
  public loading: boolean = false;
  private filterAgentMessage = false;
  private messageRenderDur: any = monitor.emitDuration(
    ImcloudInfo.MessageRender,
  );

  get byteIM() {
    return this.instance;
  }
  constructor({
    initDialogInfo,
    filterAgentMessage,
    initLoadSize,
    ...config
  }: ChannelInitProps) {
    super();
    this.initLoadSize = initLoadSize || 20;
    this.imcloudConfig = config;
    this.filterAgentMessage = filterAgentMessage || false;
    const plugins: any[] = [MultimediaPlugin, ExtensionPlugin, MetricsPlugin];
    if (isIndexedDBSupport() && config.storage) {
      plugins.push(StoragePlugin);
    }
    this.instance = new BytedIM(config, plugins);
  }

  get messageList() {
    return this.getMessages();
  }

  get conversationInfo() {
    return this.instance.getConversationList()?.[0];
  }

  get dialog() {
    return (
      this.instance.getConversationList()?.[0] || this.appContext?.dialog || {}
    );
  }

  public destory() {
    this.instance.dispose();
    super.destroy();
  }

  public pullMessages = async (
    params: {
      cursor?: string;
      conversation?: Conversation;
      limit?: number;
    } = {},
  ) => {
    this.loading = true;
    if (!this.conversationInfo) return { hasMore: false, messages: [] };

    try {
      const res = await this.instance?.getMessagesByConversation({
        conversation: this.conversationInfo,
        cursor: this.cursor,
        limit: 20,
        ...params,
      });
      this.cursor = res?.cursor;
      this.loading = false;
      return res;
    } catch (e) {
      this.loading = false;
      throw e;
    }
  };
  /**
   * @description Pull history session
   * @returns void
   */
  public pullHistoryMessages = async () => {
    // If there is no dialogId, there is no need to execute down
    if (!this.conversationInfo) return { hasMore: false, messages: [] };

    try {
      const res = await this.instance?.getMessagesByConversation({
        conversation: this.conversationInfo,
        cursor: this.cursor || '0',
        limit: 20,
      });
      this.cursor = res?.cursor;
      return res;
    } catch (e) {
      throw e;
    }
  };
  /**
   * @description Read message
   * @param readIndex: orderInDialog
   * @returns
   */
  public read = (readIndex?: string) => {
    if (this.conversationInfo) {
      return this.instance?.markConversationRead({
        conversation: this.conversationInfo,
        readIndex: readIndex,
      });
    }
  };

  public getMessages = () => {
    return (
      this.conversationInfo?.getMessageList?.((message: Message) => {
        return (
          message.visible &&
          (typeof this.cursor === 'undefined' ||
            message.indexInConversation.gte(this.cursor))
        );
      }) || []
    );
  };

  /**
   * @description Send message
   * @param appContext
   * @param message
   * @returns
   */
  public sendMessage = async (
    appContext: IAppContext<CombineMessageType>,
    message: Message,
  ) => {
    if (message.fileInfo?.fileHandler) {
      return appContext.utils?.sendFile(message);
    } else {
      return appContext.utils?.sendMessage(message);
    }
  };

  /**
   * @description Retract message
   * @param serverId
   * @returns
   */
  public recallMessage = async (serverId: string) => {
    const messages = this.messageList;
    const message = messages.find(
      (message: Message) => message.serverId === serverId,
    );
    if (message) {
      const result = await this.instance.recallMessage({
        message,
      });

      return result;
    }

    throw 'no message';
  };

  /**
   * @description Refresh media message
   * @param serverId
   * @returns
   */
  public refreshFileUrl = async (
    serverId: string,
    createAtTimestamp: number,
  ) => {
    // 1h内不去刷链接
    try {
      if (createAtTimestamp) {
        const mediaExpirationTime = 1 * 60 * 60 * 1000; // 1h
        const timeNow = new Date().getTime();
        if (mediaExpirationTime + createAtTimestamp > timeNow) return;
      }
    } catch (error) {}
    const message = await this.instance.getMessageByServerId({
      conversation: this.conversationInfo,
      serverMessageId: serverId,
    });
    if (message) {
      const result = await this.instance.refreshFileUrl({
        message,
      });
      return result;
    }
    throw 'no message';
  };

  /**
   * @description Resend message
   * @param clientId
   * @returns
   */
  public resendMessage = async (clientId: string) => {
    const messages = this.messageList;
    const message = messages.find((message) => message.clientId === clientId);

    if (message) {
      const result = await this.instance.sendMessage({
        message,
      });
      return result;
    }
    throw 'no message';
  };

  public transformDialog = () => {
    const {
      dialogId: imcloudExtDialogId,
      type,
      status,
      ...dialogExtRest
    } = this.dialog?.ext || {};
    const { id: createInfoDialogId, ...createInfo } =
      this.appContext?.dialogInfo || {};
    // 如果当前创建会话信息的dialogId大于imcloud的dialogId 赋较大值
    const linkchatDialogId =
      imcloudExtDialogId! < createInfoDialogId!
        ? createInfoDialogId
        : imcloudExtDialogId;

    return {
      ...createInfo,
      dialogId: linkchatDialogId,
      id: this.dialog.id,
      shortId: this.dialog.shortId,
      conversationId: this.dialog.id, // Conversation id, chat id alone
      conversationShortId: this.dialog.shortId, // Conversation short id, group chat id
      unreadCount: this.dialog.unreadCount, // Latitude number of messages
      firstPageParticipant: this.dialog.firstPageParticipant,
      participants: this.dialog.participants,
      lastMessage: this.dialog.lastMessage,
      lastMessageIndex: this.dialog.lastMessageIndex,
      lastMessageId:
        this.dialog?.lastMessage?.clientId ||
        this.dialog?.lastMessage?.serverId,
      customerId: this.dialog.coreInfo?.owner, // 用户id
      customerName: this.dialog.coreInfo?.name, // 用户姓名
      ext: {
        ...dialogExtRest,
        dialogId: linkchatDialogId,
        type: !isUndefined(type) ? Number(type) : null,
        status: !isUndefined(status) ? Number(status) : null,
      },
    };
  };

  // Message => IMessage
  public transMessage = (message: Message | never): CombineMessageType => {
    const {
      content,
      conversationId,
      conversationShortId,
      conversationType,
      createdAt,
      isFromMe,
      sender,
      clientId,
      serverId,
      type,
      flightStatus,
      isRecalled,
      ext,
      ...otherProps
    } = message;
    return {
      ...otherProps,
      _id: clientId || serverId,
      content,
      conversationId,
      conversationShortId,
      conversationType,
      createdAt,
      createdTime: createdAt.getTime(),
      isFromMe,
      sender: String(sender),
      senderType: ext.senderType,
      clientId,
      serverId,
      type,
      flightStatus,
      isRecalled,
      recalled: isRecalled,
      recall: isRecalled,
      ext: { ...ext },
    } as CombineMessageType;
  };

  // Trigger rerender, currently released for AD
  public emitChange = () => {
    if (this.loading || !this.filterAgentMessage) return;
    this.emitDialogRefresh();
  };

  // 刷新会话
  public emitDialogRefresh = () => {
    const dialogList = this.instance.getConversationList();
    if (Array.isArray(dialogList) && dialogList.length > 0) {
      const [dialog] = dialogList;
      const messages = dialog.getMessageList();

      if (messages.length) {
        this.messageRenderDur &&
          this.messageRenderDur({
            length: messages.length,
          });
        this.messageRenderDur = null;
      }
      this.emitDialogChange(this.transformDialog());
      // Filter messages only for the human session phase
      if (this.filterAgentMessage) {
        let currentDialogId: string,
          currentDialogType: string,
          currentDialogStatus: string;
        if (this.appContext?.dialogInfo?.id) {
          currentDialogId = this.appContext.dialogInfo?.id;
          currentDialogType = this.appContext.dialogInfo?.type;
          currentDialogStatus = this.appContext.dialogInfo?.status;
        } else {
          currentDialogId = dialog.ext?.dialogId;
          currentDialogType = dialog.ext?.type;
          currentDialogStatus = dialog.ext?.status;
        }
        this.emitMessagesChange(
          messages
            .map(this.transMessage)
            .filter(
              (item) =>
                +item.ext?.dialogId === +currentDialogId &&
                (+currentDialogType === +DIALOG_TYPE.AGENT ||
                  +currentDialogType === +DIALOG_TYPE.OFFLINE ||
                  +currentDialogStatus === DIALOG_STATUS.QUEUEING),
            ),
        );
      } else {
        this.emitMessagesChange(messages.map(this.transMessage));
      }
    } else {
      this.emitDialogChange(this.transformDialog());
      this.emitMessagesChange([]);
    }
  };

  init = async (appContext: IAppContext<CombineMessageType>) => {
    if (!this.instance) return;

    this.appContext = appContext;
    this.emitDialogChange(appContext.dialog);

    this.instance.event.subscribe(IMEvent.InitLoadPage, () => {
      logger.info(ImcloudInfo.InitLoadPage, {
        dialogExt: JSON.stringify(this.dialog.ext),
      });
      // 首屏消息补充 非缓存或者消息未加载满
      this.emitDialogRefresh();
    });

    this.instance.event.subscribe(IMEvent.MessageSend, async () => {
      this.emitDialogRefresh();
    });

    this.instance.event.subscribe(
      IMEvent.ReceiveNewMessage,
      async (message: Message) => {
        if (Date.now() - message?.createdAt?.getTime() > 1000) {
          monitor.emitDelay(
            ImcloudInfo.ReceiveNewMessage,
            Date.now() - message?.createdAt?.getTime(),
            {
              ...message.ext,
              now: Date.now(),
              created: message?.createdAt?.getTime(),
            },
          );
        }
        this.emitMessageChange(message);
        this.emitDialogRefresh();
      },
    );
    this.instance.event.subscribe(IMEvent.ConversationChange, async () => {
      logger.info(ImcloudInfo.ConversationChange, {
        dialogExt: JSON.stringify(this.dialog.ext),
        conversationId: this.dialog.id,
        lastMsgId: this.dialog?.lastMessage?.serverId,
      });
      this.emitDialogRefresh();
    });

    this.instance.event.subscribe(IMEvent.WebSocketConnected, (socket: any) => {
      this.instance.getMessagesByUser().catch(() => {});
      monitor.emitCounter(ImcloudInfo.WebSocketConnected);
      logger.info(ImcloudInfo.WebSocketConnected, {
        socketurl: socket?.ws?.url,
      });
    });

    this.instance.event.subscribe(
      IMEvent.WebSocketDisconnected,
      (socket: any) => {
        monitor.emitCounter(ImcloudInfo.WebSocketDisconnected);
        logger.info(ImcloudInfo.WebSocketDisconnected, {
          socketurl: socket?.ws?.url,
          config: this.imcloudConfig,
        });
      },
    );

    this.instance.event.subscribe(IMEvent.Error, (e) => {
      monitor.emitCounter(ImcloudInfo.WsError);
      logger.error(ImcloudInfo.WsError, e);
    });

    try {
      const imCloudInstance = this.instance.init({
        msgLimit: this.initLoadSize,
      });
      const initResult = await imCloudInstance;
      if (initResult !== InitResult.Succeeded) {
        throw new Error('BytedIM instance init error');
      }

      monitor.emitCounter(ImcloudInfo.SdkInitSuccess);
      /** 补充会话链变更 */
      this.instance.getUserConversationList({
        type: im_proto.ConversationType.GROUP_CHAT,
      });
      return imCloudInstance;
    } catch (e) {
      monitor.emitCounter(ImcloudInfo.SdkInitFail);
      logger.error('BytedIM instance init error', {
        error: e,
        config: this.imcloudConfig,
      });
      throw e;
    }
  };
}

export default Channel;
